/**
 * Service Ticket System
 * Manages service tickets with vendor collaboration via public links
 */

export type ServiceType =
  | 'oil_hauling'
  | 'water_hauling'
  | 'swabbing'
  | 'workover'
  | 'hot_oil'
  | 'pump_repair'
  | 'compressor_service'
  | 'electrical'
  | 'welding'
  | 'inspection'
  | 'parts_delivery'
  | 'emergency'
  | 'other';

export type TicketStatus =
  | 'draft'
  | 'pending_approval' // Awaiting manager approval
  | 'approved' // Manager approved, ready to send to vendor
  | 'open'
  | 'assigned'
  | 'vendor_accepted' // Vendor accepted the job
  | 'in_progress'
  | 'pending_parts'
  | 'completed'
  | 'cancelled'
  | 'rejected' // Manager rejected
  | 'on_hold';

export type TicketPriority = 'low' | 'medium' | 'high' | 'emergency';

export type ContactMethod = 'email' | 'phone' | 'sms' | 'text';

export type VendorResponseStatus = 
  | 'pending' // Waiting for vendor response
  | 'accepted' // Vendor accepted the job
  | 'declined' // Vendor declined the job
  | 'in_progress' // Vendor is working on it
  | 'completed'; // Vendor marked as complete

export interface ServiceTicket {
  id: string;
  ticketNumber: string;
  serviceType: ServiceType;
  title: string;
  description: string;
  priority: TicketPriority;
  status: TicketStatus;
  
  // Location
  wellId?: string;
  wellName?: string;
  location: string;
  coordinates?: { lat: number; lng: number };
  
  // Assignment
  assignedTo?: {
    type: 'user' | 'vendor';
    id: string;
    name: string;
    contact?: string;
  };
  requestedBy: {
    id: string;
    name: string;
  };
  
  // Manager Approval Workflow
  managerReview?: {
    status: 'pending' | 'approved' | 'rejected';
    reviewedBy?: {
      id: string;
      name: string;
    };
    reviewedAt?: Date;
    comments?: string;
    changes?: string; // What the manager changed/edited
  };
  
  // Vendor Response
  vendorResponse?: {
    status: VendorResponseStatus;
    acceptedAt?: Date;
    declinedAt?: Date;
    declineReason?: string;
    estimatedArrival?: Date;
    startedAt?: Date;
    completedAt?: Date;
    technicianName?: string;
    technicianPhone?: string;
  };
  
  // Scheduling
  requestedDate: Date;
  scheduledDate?: Date;
  completedDate?: Date;
  estimatedDuration?: number; // hours
  
  // Vendor Access
  publicLink?: string;
  publicLinkActive: boolean;
  publicLinkExpires?: Date;
  
  // Details
  notes: string;
  voiceMemos: VoiceMemo[];
  photos: Photo[];
  attachments: Attachment[];
  
  // Cost & Invoicing
  estimatedCost?: number;
  actualCost?: number;
  partsUsed: Array<{ partId: string; partName: string; quantity: number; cost: number }>;
  laborHours?: number;
  laborRate?: number;
  invoiceUrl?: string; // URL to uploaded invoice from vendor
  invoiceNumber?: string;
  invoiceDate?: Date;
  invoiceApproved?: boolean;
  invoiceApprovedBy?: {
    id: string;
    name: string;
    approvedAt: Date;
  };
  
  // Updates
  updates: TicketUpdate[];
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: {
    id: string;
    name: string;
  };
}

export interface VoiceMemo {
  id: string;
  url: string;
  duration: number;
  transcript?: string;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
}

export interface Photo {
  id: string;
  url: string;
  thumbnail?: string;
  caption?: string;
  location?: { lat: number; lng: number };
  createdBy: string;
  createdByName: string;
  createdAt: Date;
}

export interface Attachment {
  id: string;
  filename: string;
  url: string;
  type: string;
  size: number;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
}

export interface TicketUpdate {
  id: string;
  ticketId: string;
  type: 'status_change' | 'assignment' | 'comment' | 'cost_update' | 'schedule_change';
  message: string;
  previousValue?: any;
  newValue?: any;
  createdBy: {
    type: 'user' | 'vendor' | 'system';
    id: string;
    name: string;
  };
  createdAt: Date;
}

export interface Vendor {
  id: string;
  companyName: string;
  contactPerson: {
    firstName: string;
    lastName: string;
    title?: string;
  };
  
  // Contact Information
  email: string;
  phone: string;
  smsNumber?: string;
  alternatePhone?: string;
  preferredContactMethod: ContactMethod; // How they prefer to be contacted
  
  // Address
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  location?: { lat: number; lng: number };
  
  // Services & Availability
  services: ServiceType[];
  emergencyAvailable: boolean;
  emergencyPhone?: string; // Special emergency contact number
  serviceArea: string[]; // Cities/regions they service
  
  // Rates & Pricing
  hourlyRate?: number;
  callOutFee?: number; // Flat fee just to show up
  emergencyRate?: number; // Higher rate for emergency calls
  mileageRate?: number; // Cost per mile
  minimumCharge?: number;
  rateNotes?: string;
  
  // Performance Metrics
  rating: number;
  totalJobs: number;
  completedJobs: number;
  acceptedJobs: number;
  declinedJobs: number;
  averageResponseTime: number; // hours to respond to request
  averageCompletionTime: number; // hours to complete job
  onTimePercentage: number;
  
  // Qualifications
  active: boolean;
  certifications?: string[];
  insurance?: {
    provider: string;
    policyNumber: string;
    expirationDate: Date;
    verified: boolean;
  };
  license?: {
    number: string;
    state: string;
    expirationDate: Date;
    verified: boolean;
  };
  
  // Financial
  paymentTerms?: string; // "Net 30", "Due on receipt", etc.
  taxId?: string;
  
  // Notes & History
  notes?: string;
  internalNotes?: string; // Private notes not visible to vendor
  lastJobDate?: Date;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy?: {
    id: string;
    name: string;
  };
}

class TicketSystem {
  private tickets: ServiceTicket[] = [];
  private vendors: Vendor[] = [];
  private publicLinks: Map<string, string> = new Map(); // publicLink -> ticketId

  constructor() {
    this.loadFromStorage();
  }

  // ============= TICKET MANAGEMENT =============

  createTicket(
    ticket: Omit<ServiceTicket, 'id' | 'ticketNumber' | 'publicLink' | 'publicLinkActive' | 'updates' | 'createdAt' | 'updatedAt'>
  ): ServiceTicket {
    const ticketNumber = this.generateTicketNumber();
    const publicLink = this.generatePublicLink();

    const newTicket: ServiceTicket = {
      ...ticket,
      id: `ticket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ticketNumber,
      publicLink,
      publicLinkActive: false, // Activated when assigned to vendor
      updates: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.tickets.push(newTicket);
    this.publicLinks.set(publicLink, newTicket.id);
    this.saveToStorage();

    return newTicket;
  }

  updateTicket(
    id: string,
    updates: Partial<ServiceTicket>,
    updatedBy: { type: 'user' | 'vendor' | 'system'; id: string; name: string }
  ): ServiceTicket | null {
    const ticket = this.getTicket(id);
    if (!ticket) return null;

    // Track changes
    const changes: TicketUpdate[] = [];

    // Status change
    if (updates.status && updates.status !== ticket.status) {
      changes.push({
        id: `update-${Date.now()}`,
        ticketId: id,
        type: 'status_change',
        message: `Status changed from ${ticket.status} to ${updates.status}`,
        previousValue: ticket.status,
        newValue: updates.status,
        createdBy: updatedBy,
        createdAt: new Date(),
      });
    }

    // Assignment change
    if (updates.assignedTo && updates.assignedTo !== ticket.assignedTo) {
      changes.push({
        id: `update-${Date.now()}-1`,
        ticketId: id,
        type: 'assignment',
        message: updates.assignedTo
          ? `Assigned to ${updates.assignedTo.name}`
          : 'Assignment removed',
        previousValue: ticket.assignedTo,
        newValue: updates.assignedTo,
        createdBy: updatedBy,
        createdAt: new Date(),
      });

      // Activate public link if assigned to vendor
      if (updates.assignedTo?.type === 'vendor') {
        updates.publicLinkActive = true;
        updates.publicLinkExpires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days
      }
    }

    // Cost update
    if (updates.actualCost !== undefined && updates.actualCost !== ticket.actualCost) {
      changes.push({
        id: `update-${Date.now()}-2`,
        ticketId: id,
        type: 'cost_update',
        message: `Cost updated to $${updates.actualCost}`,
        previousValue: ticket.actualCost,
        newValue: updates.actualCost,
        createdBy: updatedBy,
        createdAt: new Date(),
      });
    }

    // Apply updates
    Object.assign(ticket, updates);
    ticket.updates.push(...changes);
    ticket.updatedAt = new Date();

    // Deactivate public link if ticket is completed or cancelled
    if (ticket.status === 'completed' || ticket.status === 'cancelled') {
      ticket.publicLinkActive = false;
    }

    this.saveToStorage();
    return ticket;
  }

  addTicketComment(
    ticketId: string,
    comment: string,
    createdBy: { type: 'user' | 'vendor' | 'system'; id: string; name: string }
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'comment',
      message: comment,
      createdBy,
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  addVoiceMemo(
    ticketId: string,
    memo: Omit<VoiceMemo, 'id' | 'createdAt'>
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    const voiceMemo: VoiceMemo = {
      ...memo,
      id: `voice-${Date.now()}`,
      createdAt: new Date(),
    };

    ticket.voiceMemos.push(voiceMemo);
    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  addPhoto(
    ticketId: string,
    photo: Omit<Photo, 'id' | 'createdAt'>
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    const newPhoto: Photo = {
      ...photo,
      id: `photo-${Date.now()}`,
      createdAt: new Date(),
    };

    ticket.photos.push(newPhoto);
    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  getTicket(id: string): ServiceTicket | null {
    return this.tickets.find(t => t.id === id) || null;
  }

  getTicketByNumber(ticketNumber: string): ServiceTicket | null {
    return this.tickets.find(t => t.ticketNumber === ticketNumber) || null;
  }

  getTicketByPublicLink(publicLink: string): ServiceTicket | null {
    const ticketId = this.publicLinks.get(publicLink);
    if (!ticketId) return null;

    const ticket = this.getTicket(ticketId);
    
    // Verify link is active and not expired
    if (!ticket || !ticket.publicLinkActive) return null;
    if (ticket.publicLinkExpires && ticket.publicLinkExpires < new Date()) {
      return null;
    }

    return ticket;
  }

  getAllTickets(): ServiceTicket[] {
    return [...this.tickets].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  getTicketsByStatus(status: TicketStatus): ServiceTicket[] {
    return this.tickets.filter(t => t.status === status);
  }

  getTicketsByVendor(vendorId: string): ServiceTicket[] {
    return this.tickets.filter(
      t => t.assignedTo?.type === 'vendor' && t.assignedTo.id === vendorId
    );
  }

  getOpenTickets(): ServiceTicket[] {
    return this.tickets.filter(
      t => !['completed', 'cancelled'].includes(t.status)
    );
  }

  // ============= VENDOR MANAGEMENT =============

  createVendor(vendor: Omit<Vendor, 'id' | 'rating' | 'totalJobs' | 'completedJobs' | 'acceptedJobs' | 'declinedJobs' | 'averageResponseTime' | 'averageCompletionTime' | 'onTimePercentage' | 'createdAt' | 'updatedAt'>): Vendor {
    const newVendor: Vendor = {
      ...vendor,
      id: `vendor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      rating: 0,
      totalJobs: 0,
      completedJobs: 0,
      acceptedJobs: 0,
      declinedJobs: 0,
      averageResponseTime: 0,
      averageCompletionTime: 0,
      onTimePercentage: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.vendors.push(newVendor);
    this.saveToStorage();

    return newVendor;
  }

  updateVendor(id: string, updates: Partial<Vendor>): Vendor | null {
    const vendor = this.vendors.find(v => v.id === id);
    if (!vendor) return null;

    Object.assign(vendor, updates);
    vendor.updatedAt = new Date();
    this.saveToStorage();

    return vendor;
  }

  deleteVendor(id: string): boolean {
    const index = this.vendors.findIndex(v => v.id === id);
    if (index === -1) return false;

    this.vendors.splice(index, 1);
    this.saveToStorage();
    return true;
  }

  getVendor(id: string): Vendor | null {
    return this.vendors.find(v => v.id === id) || null;
  }

  getAllVendors(): Vendor[] {
    return [...this.vendors];
  }

  getVendorsByService(serviceType: ServiceType): Vendor[] {
    return this.vendors.filter(v => v.active && v.services.includes(serviceType));
  }

  getTopRatedVendors(limit: number = 5): Vendor[] {
    return [...this.vendors]
      .filter(v => v.active)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, limit);
  }

  updateVendorRating(vendorId: string, rating: number): void {
    const vendor = this.getVendor(vendorId);
    if (!vendor) return;

    // Simple moving average
    const totalRatings = vendor.totalJobs;
    vendor.rating = ((vendor.rating * totalRatings) + rating) / (totalRatings + 1);
    
    this.saveToStorage();
  }

  // ============= MANAGER APPROVAL WORKFLOW =============

  submitTicketForApproval(
    ticketId: string,
    submittedBy: { id: string; name: string }
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'pending_approval';
    ticket.managerReview = {
      status: 'pending',
    };

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: 'Ticket submitted for manager approval',
      previousValue: ticket.status,
      newValue: 'pending_approval',
      createdBy: { ...submittedBy, type: 'user' },
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  approveTicket(
    ticketId: string,
    reviewedBy: { id: string; name: string },
    comments?: string,
    changes?: string
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'approved';
    ticket.managerReview = {
      status: 'approved',
      reviewedBy,
      reviewedAt: new Date(),
      comments,
      changes,
    };

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: `Ticket approved by ${reviewedBy.name}${comments ? `: ${comments}` : ''}`,
      previousValue: 'pending_approval',
      newValue: 'approved',
      createdBy: { ...reviewedBy, type: 'user' },
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  rejectTicket(
    ticketId: string,
    reviewedBy: { id: string; name: string },
    comments: string
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'rejected';
    ticket.managerReview = {
      status: 'rejected',
      reviewedBy,
      reviewedAt: new Date(),
      comments,
    };

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: `Ticket rejected by ${reviewedBy.name}: ${comments}`,
      previousValue: 'pending_approval',
      newValue: 'rejected',
      createdBy: { ...reviewedBy, type: 'user' },
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  // ============= VENDOR RESPONSE MANAGEMENT =============

  vendorAcceptTicket(
    ticketId: string,
    technicianName: string,
    technicianPhone: string,
    estimatedArrival?: Date
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'vendor_accepted';
    ticket.vendorResponse = {
      status: 'accepted',
      acceptedAt: new Date(),
      technicianName,
      technicianPhone,
      estimatedArrival,
    };

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: `Vendor accepted job. Technician: ${technicianName}${estimatedArrival ? `, ETA: ${estimatedArrival.toLocaleString()}` : ''}`,
      previousValue: ticket.status,
      newValue: 'vendor_accepted',
      createdBy: { type: 'vendor', id: ticket.assignedTo!.id, name: ticket.assignedTo!.name },
      createdAt: new Date(),
    });

    // Update vendor stats
    if (ticket.assignedTo?.type === 'vendor') {
      const vendor = this.getVendor(ticket.assignedTo.id);
      if (vendor) {
        vendor.acceptedJobs++;
        vendor.lastJobDate = new Date();
        this.saveToStorage();
      }
    }

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  vendorDeclineTicket(
    ticketId: string,
    reason: string
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'open'; // Back to open for reassignment
    ticket.vendorResponse = {
      status: 'declined',
      declinedAt: new Date(),
      declineReason: reason,
    };

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: `Vendor declined job: ${reason}`,
      previousValue: ticket.status,
      newValue: 'open',
      createdBy: { type: 'vendor', id: ticket.assignedTo!.id, name: ticket.assignedTo!.name },
      createdAt: new Date(),
    });

    // Update vendor stats
    if (ticket.assignedTo?.type === 'vendor') {
      const vendor = this.getVendor(ticket.assignedTo.id);
      if (vendor) {
        vendor.declinedJobs++;
        this.saveToStorage();
      }
    }

    // Clear assignment
    ticket.assignedTo = undefined;
    ticket.publicLinkActive = false;

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  vendorStartWork(
    ticketId: string
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'in_progress';
    if (ticket.vendorResponse) {
      ticket.vendorResponse.status = 'in_progress';
      ticket.vendorResponse.startedAt = new Date();
    }

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: 'Vendor started work',
      previousValue: ticket.status,
      newValue: 'in_progress',
      createdBy: { type: 'vendor', id: ticket.assignedTo!.id, name: ticket.assignedTo!.name },
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  vendorCompleteWork(
    ticketId: string,
    actualCost?: number,
    laborHours?: number,
    notes?: string
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.status = 'completed';
    ticket.completedDate = new Date();
    
    if (ticket.vendorResponse) {
      ticket.vendorResponse.status = 'completed';
      ticket.vendorResponse.completedAt = new Date();
    }

    if (actualCost !== undefined) ticket.actualCost = actualCost;
    if (laborHours !== undefined) ticket.laborHours = laborHours;

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'status_change',
      message: `Vendor completed work${notes ? `: ${notes}` : ''}${actualCost ? `. Cost: $${actualCost}` : ''}`,
      previousValue: 'in_progress',
      newValue: 'completed',
      createdBy: { type: 'vendor', id: ticket.assignedTo!.id, name: ticket.assignedTo!.name },
      createdAt: new Date(),
    });

    // Update vendor stats
    if (ticket.assignedTo?.type === 'vendor') {
      const vendor = this.getVendor(ticket.assignedTo.id);
      if (vendor) {
        vendor.completedJobs++;
        vendor.totalJobs++;
        
        // Calculate average completion time
        if (ticket.vendorResponse?.startedAt && ticket.vendorResponse?.completedAt) {
          const completionTime = (ticket.vendorResponse.completedAt.getTime() - ticket.vendorResponse.startedAt.getTime()) / (1000 * 60 * 60);
          vendor.averageCompletionTime = ((vendor.averageCompletionTime * (vendor.completedJobs - 1)) + completionTime) / vendor.completedJobs;
        }
        
        this.saveToStorage();
      }
    }

    ticket.publicLinkActive = false;
    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  // ============= INVOICE MANAGEMENT =============

  uploadInvoice(
    ticketId: string,
    invoiceUrl: string,
    invoiceNumber: string,
    invoiceDate: Date,
    amount: number
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    ticket.invoiceUrl = invoiceUrl;
    ticket.invoiceNumber = invoiceNumber;
    ticket.invoiceDate = invoiceDate;
    ticket.actualCost = amount;
    ticket.invoiceApproved = false;

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'comment',
      message: `Invoice uploaded: ${invoiceNumber} - $${amount}`,
      createdBy: { type: 'vendor', id: ticket.assignedTo!.id, name: ticket.assignedTo!.name },
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  approveInvoice(
    ticketId: string,
    approvedBy: { id: string; name: string }
  ): ServiceTicket | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket || !ticket.invoiceUrl) return null;

    ticket.invoiceApproved = true;
    ticket.invoiceApprovedBy = {
      ...approvedBy,
      approvedAt: new Date(),
    };

    ticket.updates.push({
      id: `update-${Date.now()}`,
      ticketId,
      type: 'comment',
      message: `Invoice approved by ${approvedBy.name}`,
      createdBy: { type: 'user', id: approvedBy.id, name: approvedBy.name },
      createdAt: new Date(),
    });

    ticket.updatedAt = new Date();
    this.saveToStorage();

    return ticket;
  }

  getTicketsByInvoiceStatus(approved: boolean): ServiceTicket[] {
    return this.tickets.filter(t => 
      t.invoiceUrl && t.invoiceApproved === approved
    );
  }

  getTicketsPendingApproval(): ServiceTicket[] {
    return this.tickets.filter(t => t.status === 'pending_approval');
  }

  // ============= PUBLIC LINK SYSTEM =============

  private generatePublicLink(): string {
    return `${Math.random().toString(36).substr(2, 9)}-${Date.now().toString(36)}`;
  }

  private generateTicketNumber(): string {
    const year = new Date().getFullYear();
    const count = this.tickets.length + 1;
    return `TKT-${year}-${String(count).padStart(5, '0')}`;
  }

  regeneratePublicLink(ticketId: string): string | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket) return null;

    // Remove old link
    if (ticket.publicLink) {
      this.publicLinks.delete(ticket.publicLink);
    }

    // Generate new link
    const newLink = this.generatePublicLink();
    ticket.publicLink = newLink;
    this.publicLinks.set(newLink, ticketId);

    this.saveToStorage();
    return newLink;
  }

  // ============= NOTIFICATIONS =============

  generateVendorNotification(ticketId: string): {
    sms: string;
    email: { subject: string; body: string; html: string };
    link: string;
  } | null {
    const ticket = this.getTicket(ticketId);
    if (!ticket || !ticket.assignedTo || ticket.assignedTo.type !== 'vendor') {
      return null;
    }

    const vendor = this.getVendor(ticket.assignedTo.id);
    if (!vendor) return null;

    const link = `${window.location.origin}/vendor-portal/${ticket.publicLink}`;

    const sms = `New service request from OilWellX. Ticket #${ticket.ticketNumber}: ${ticket.title}. View details: ${link}`;

    const email = {
      subject: `New Service Request - Ticket #${ticket.ticketNumber}`,
      body: `
Hello ${vendor.contactPerson.firstName},

You have been assigned a new service request.

Ticket: ${ticket.ticketNumber}
Service Type: ${ticket.serviceType.replace(/_/g, ' ').toUpperCase()}
Priority: ${ticket.priority.toUpperCase()}
Location: ${ticket.location}

Description:
${ticket.description}

Scheduled Date: ${ticket.scheduledDate ? new Date(ticket.scheduledDate).toLocaleDateString() : 'TBD'}
Estimated Cost: ${ticket.estimatedCost ? `$${ticket.estimatedCost}` : 'TBD'}

Please click the link below to view full details and update the ticket:
${link}

This link will remain active until the ticket is completed.

Thank you,
OilWellX Team
      `.trim(),
      html: `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; }
    .content { background: #f9fafb; padding: 30px; }
    .ticket-info { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .info-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
    .info-label { font-weight: bold; color: #6b7280; }
    .priority-${ticket.priority} { 
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: bold;
      ${ticket.priority === 'emergency' ? 'background: #fee; color: #dc2626;' : 
        ticket.priority === 'high' ? 'background: #fef3c7; color: #d97706;' :
        ticket.priority === 'medium' ? 'background: #dbeafe; color: #2563eb;' :
        'background: #f3f4f6; color: #6b7280;'}
    }
    .button { 
      display: inline-block;
      background: #2563eb;
      color: white;
      padding: 15px 30px;
      text-decoration: none;
      border-radius: 8px;
      font-weight: bold;
      margin: 20px 0;
    }
    .footer { text-align: center; color: #6b7280; font-size: 12px; padding: 20px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">New Service Request</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">Ticket #${ticket.ticketNumber}</p>
    </div>
    <div class="content">
      <p>Hello ${vendor.contactPerson.firstName},</p>
      <p>You have been assigned a new service request that requires your attention.</p>
      
      <div class="ticket-info">
        <div class="info-row">
          <span class="info-label">Service Type:</span>
          <span>${ticket.serviceType.replace(/_/g, ' ').toUpperCase()}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Priority:</span>
          <span class="priority-${ticket.priority}">${ticket.priority.toUpperCase()}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Location:</span>
          <span>${ticket.location}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Scheduled Date:</span>
          <span>${ticket.scheduledDate ? new Date(ticket.scheduledDate).toLocaleDateString() : 'TBD'}</span>
        </div>
        <div class="info-row" style="border-bottom: none;">
          <span class="info-label">Estimated Cost:</span>
          <span>${ticket.estimatedCost ? `$${ticket.estimatedCost.toFixed(2)}` : 'TBD'}</span>
        </div>
      </div>

      <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h3 style="margin-top: 0;">Description:</h3>
        <p>${ticket.description}</p>
      </div>

      <div style="text-align: center;">
        <a href="${link}" class="button">View Ticket Details</a>
      </div>

      <p style="font-size: 14px; color: #6b7280;">
        <strong>Note:</strong> This link will remain active until the ticket is completed. 
        You can update the ticket status, add photos, and communicate with the team through this portal.
      </p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} OilWellX. All rights reserved.</p>
      <p>This is an automated notification. Please do not reply to this email.</p>
    </div>
  </div>
</body>
</html>
      `.trim(),
    };

    return { sms, email, link };
  }

  // ============= STORAGE =============

  private saveToStorage(): void {
    try {
      localStorage.setItem('ticket_system', JSON.stringify({
        tickets: this.tickets,
        vendors: this.vendors,
        publicLinks: Array.from(this.publicLinks.entries()),
      }));
    } catch (error) {
      console.error('Failed to save ticket system data:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('ticket_system');
      if (!stored) {
        this.initializeMockData();
        return;
      }

      const data = JSON.parse(stored);
      this.tickets = data.tickets || [];
      this.vendors = data.vendors || [];
      this.publicLinks = new Map(data.publicLinks || []);

      // Convert date strings back to Date objects
      this.tickets.forEach(t => {
        t.requestedDate = new Date(t.requestedDate);
        t.createdAt = new Date(t.createdAt);
        t.updatedAt = new Date(t.updatedAt);
        if (t.scheduledDate) t.scheduledDate = new Date(t.scheduledDate);
        if (t.completedDate) t.completedDate = new Date(t.completedDate);
        if (t.publicLinkExpires) t.publicLinkExpires = new Date(t.publicLinkExpires);
        t.updates.forEach(u => u.createdAt = new Date(u.createdAt));
        t.voiceMemos.forEach(v => v.createdAt = new Date(v.createdAt));
        t.photos.forEach(p => p.createdAt = new Date(p.createdAt));
        t.attachments.forEach(a => a.createdAt = new Date(a.createdAt));
      });

      this.vendors.forEach(v => {
        v.createdAt = new Date(v.createdAt);
        v.updatedAt = new Date(v.updatedAt);
        if (v.insurance?.expirationDate) {
          v.insurance.expirationDate = new Date(v.insurance.expirationDate);
        }
      });
    } catch (error) {
      console.error('Failed to load ticket system data:', error);
      this.initializeMockData();
    }
  }

  private initializeMockData(): void {
    // Create sample vendor
    this.createVendor({
      companyName: 'ABC Oil Services',
      contactPerson: {
        firstName: 'John',
        lastName: 'Doe',
        title: 'Operations Manager',
      },
      email: 'john@abcoilservices.com',
      phone: '555-0123',
      smsNumber: '555-0123',
      preferredContactMethod: 'sms',
      address: {
        street: '123 Oil Field Rd',
        city: 'Houston',
        state: 'TX',
        zip: '77001',
      },
      services: ['oil_hauling', 'water_hauling', 'hot_oil'],
      serviceArea: ['Houston', 'Galveston', 'Beaumont'],
      active: true,
      emergencyAvailable: true,
      hourlyRate: 125,
      emergencyRate: 175,
      callOutFee: 75,
    });
  }
}

export const ticketSystem = new TicketSystem();
